import { CloseIcon, InfoOutlineIcon } from '@chakra-ui/icons';
import {
  Badge,
  Box,
  Button,
  Flex,
  Icon,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  fetchInvoiceJobResult,
  invoiceJobCounts,
  merchantLegalFixPath,
} from 'utils/invoiceJobApi';
import { tinVerdictLabel } from 'utils/invoiceTaxApi';

/**
 * RAT-1914: what a bulk run actually did, in three groups.
 *
 * Bulk generation cannot ask anything - an override needs a reason per invoice,
 * and nobody is watching a background job - so a recipient the tax registry
 * rejects is held back and reported here instead of stopping the batch. This is
 * a panel and not a dialog on purpose (AC6): there is no decision to take on it.
 *
 * "Submitted" is deliberately not called accepted (AC7). AEAT answers minutes to
 * hours later, and that answer shows up in the registration status on the
 * E-Invoice list, not here.
 */
const InvoiceJobResultPanel = ({ job, onClose }) => {
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasFailed, setHasFailed] = useState(false);
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.300');
  const panelBg = useColorModeValue('gray.50', 'whiteAlpha.100');

  const queueId = job?.queueId;

  useEffect(() => {
    if (!queueId) return undefined;

    let isCurrent = true;
    setIsLoading(true);
    setHasFailed(false);
    fetchInvoiceJobResult(queueId).then((data) => {
      if (!isCurrent) return;
      setResult(data?.result ?? null);
      setStatus(data?.status ?? null);
      setHasFailed(!data);
      setIsLoading(false);
    });

    return () => {
      isCurrent = false;
    };
  }, [queueId]);

  const counts = invoiceJobCounts(result, status);
  const skipped = result?.skipped ?? [];
  // Reconciled against the failures, so the group and its count agree.
  const warned = counts.warnedRows;
  const errors = result?.errors ?? [];

  const countBadge = (label, value, colorScheme) => (
    <Badge
      colorScheme={colorScheme}
      borderRadius="6px"
      px="8px"
      py="2px"
      fontSize="xs"
      textTransform="none"
    >
      {`${label}: ${value}`}
    </Badge>
  );

  const headerCell = (label) => (
    <Th
      borderColor={borderColor}
      fontSize={{ sm: '10px', lg: '11px' }}
      color="gray.400"
    >
      {label}
    </Th>
  );

  const merchantCell = (row) => (
    <Td borderColor={borderColor}>
      <Link to={merchantLegalFixPath(row?.merchantId, null)}>
        <Text color="blue.500" fontSize="sm" fontWeight="600">
          {row?.merchantName || `Merchant #${row?.merchantId}`}
        </Text>
      </Link>
    </Td>
  );

  const bodyCell = (value, color) => (
    <Td borderColor={borderColor}>
      <Text color={color || textColor} fontSize="sm">
        {value || '-'}
      </Text>
    </Td>
  );

  /**
   * AC5: the name AEAT holds, plus the one-click fix. Offered wherever a legal
   * name mismatch shows up - held back or issued with a warning - because the
   * mismatch is worth correcting for the next invoice either way. The merchant
   * page owns the save, so nothing is written from this panel.
   */
  const registryNameFix = (row) =>
    row?.registryLegalName ? (
      <>
        <Text color="gray.500" fontSize="xs" mt={1}>
          {`AEAT holds: ${row.registryLegalName}`}
        </Text>
        <Link to={merchantLegalFixPath(row?.merchantId, row.registryLegalName)}>
          <Button size="xs" variant="link" colorScheme="blue" mt={1}>
            Use AEAT legal name
          </Button>
        </Link>
      </>
    ) : null;

  return (
    <Box
      bg={panelBg}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="12px"
      p="16px"
      mb="16px"
    >
      <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
        <Flex align="center" gap={3} wrap="wrap">
          <Text fontSize="md" fontWeight="700" color={textColor}>
            {`Result - ${job?.periodLabel || 'invoice run'}`}
          </Text>
          {isLoading && <Spinner size="sm" color="orange.400" />}
        </Flex>
        <Button
          size="sm"
          variant="ghost"
          leftIcon={<Icon as={CloseIcon} w="8px" h="8px" />}
          onClick={onClose}
        >
          Close
        </Button>
      </Flex>

      {hasFailed && !isLoading && (
        <Text color="red.500" fontSize="sm" mt={2}>
          The result of this job could not be loaded. Refresh the list and open
          it again.
        </Text>
      )}

      {!isLoading && !hasFailed && (
        <>
          <Flex align="center" gap={2} wrap="wrap" mt={3}>
            {countBadge('Submitted', counts.clean, 'green')}
            {countBadge(
              'Submitted with warnings',
              counts.warningCount,
              'orange',
            )}
            {countBadge('Skipped', counts.skippedCount, 'yellow')}
            {counts.errorCount > 0 &&
              countBadge('Failed', counts.errorCount, 'red')}
            <Text color="gray.500" fontSize="sm">
              {`of ${counts.total} selected`}
            </Text>
          </Flex>

          <Flex align="flex-start" gap="6px" mt={3} color="gray.500">
            <InfoOutlineIcon boxSize={3} mt="3px" />
            <Text fontSize="sm">
              Submitted is not the same as accepted. AEAT answers minutes to
              hours later - the registration status on the E-Invoice list is
              where acceptance shows up.
            </Text>
          </Flex>

          {counts.skippedCount === 0 &&
            counts.warningCount === 0 &&
            counts.errorCount === 0 && (
              <Text color={textColor} fontSize="sm" mt={3}>
                Every invoice in this batch was submitted. No tax number was
                held back.
              </Text>
            )}

          {skipped.length > 0 && (
            <Box mt={4} overflowX="auto">
              <Text fontSize="sm" fontWeight="700" color={textColor} mb={2}>
                {`Skipped (${skipped.length}) - not invoiced`}
              </Text>
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    {headerCell('Merchant')}
                    {headerCell('Tax number')}
                    {headerCell('Registry says')}
                    {headerCell('Reason')}
                    {headerCell('What to do')}
                  </Tr>
                </Thead>
                <Tbody>
                  {skipped.map((row) => (
                    <Tr key={`skipped-${row?.merchantId}`}>
                      {merchantCell(row)}
                      {bodyCell(row?.taxNumber)}
                      {bodyCell(tinVerdictLabel(row?.verdict), 'red.500')}
                      {bodyCell(row?.message)}
                      <Td borderColor={borderColor}>
                        <Text color={textColor} fontSize="sm">
                          {row?.nextStep || '-'}
                        </Text>
                        {registryNameFix(row)}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}

          {warned.length > 0 && (
            <Box mt={4} overflowX="auto">
              <Text fontSize="sm" fontWeight="700" color={textColor} mb={2}>
                {`Submitted with warnings (${warned.length})`}
              </Text>
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    {headerCell('Merchant')}
                    {headerCell('Tax number')}
                    {headerCell('Registry says')}
                    {headerCell('Warning')}
                  </Tr>
                </Thead>
                <Tbody>
                  {warned.map((row) => (
                    <Tr key={`warned-${row?.merchantId}`}>
                      {merchantCell(row)}
                      {bodyCell(row?.taxNumber)}
                      {bodyCell(tinVerdictLabel(row?.verdict), 'orange.500')}
                      <Td borderColor={borderColor}>
                        <Text color={textColor} fontSize="sm">
                          {row?.message || '-'}
                        </Text>
                        {registryNameFix(row)}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}

          {errors.length > 0 && (
            <Box mt={4} overflowX="auto">
              <Text fontSize="sm" fontWeight="700" color={textColor} mb={2}>
                {`Failed (${errors.length}) - not invoiced`}
              </Text>
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    {headerCell('Merchant')}
                    {headerCell('Error')}
                  </Tr>
                </Thead>
                <Tbody>
                  {errors.map((row, index) => (
                    <Tr key={`error-${row?.merchantId ?? index}`}>
                      <Td borderColor={borderColor}>
                        {row?.merchantId ? (
                          <Link to={merchantLegalFixPath(row.merchantId, null)}>
                            <Text
                              color="blue.500"
                              fontSize="sm"
                              fontWeight="600"
                            >
                              {`Merchant #${row.merchantId}`}
                            </Text>
                          </Link>
                        ) : (
                          <Text color={textColor} fontSize="sm">
                            -
                          </Text>
                        )}
                      </Td>
                      {bodyCell(row?.message, 'red.500')}
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

InvoiceJobResultPanel.propTypes = {
  job: PropTypes.shape({
    queueId: PropTypes.number,
    periodLabel: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
};

export default InvoiceJobResultPanel;
